import { faArrowUpRight, faFolderGear, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { ListItem } from 'src/components/ListItem';
import { Trainer } from 'src/models/trainer/_types';
import { CreatePackageDialog } from 'src/shared/Dialogs/CreatePackageDialog';
import { Section, SectionedItemSelector } from 'src/shared/SidebarDialog';
import { selectableItemTypes as globalSelectableItemTypes } from 'src/shared/constants/itemTypes';

import { PaymentStep, usePaymentDialogContext } from './PaymentDialogContext';


interface TypeSelectorProps {
  trainer?: Trainer;
}

export const TypeSelector = ({ trainer }: TypeSelectorProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'client.payment' });
  const {
    setCurrentStep,
    selectableItemTypes: selectableItemTypesProp,
    selectedItemType,
    setSelectedItemType,
    searchValue,
    setSearchValue,
    addToCart,
    selectedUser: user,
  } = usePaymentDialogContext();

  const [showCreatePackageDialog, setShowCreatePackageDialog] = useState(false);
  const [showManageLibraryDialog, setShowManageLibraryDialog] = useState(false);
  const selectableItemTypes = globalSelectableItemTypes
    .filter((itemType) => !itemType.hide?.(trainer))
    .filter((type) =>
      selectableItemTypesProp
        ? selectableItemTypesProp.includes(type.value)
        : true
    )
    .filter((type) => {
      // When no user is selected, only show product type
      if (!user) {
        return type.value === 'product';
      }
      // When user is selected, show all types (existing behavior)
      return true;
    });
  const sections: Section[] = [
    {
      title: t('library'),
      items: selectableItemTypes.map((itemType) => ({
        label: itemType.pluralLabel,
        icon: itemType.icon,
        iconColor: 'primary' as const,
        selected: itemType.value === selectedItemType,
        onClick: () => {
          setSelectedItemType(itemType.value);
          setCurrentStep(PaymentStep.SelectItems);
        },
      })),
    },
    {
      title: t('more'),
      items: [
        {
          label: t('createPackage'),
          icon: faPenToSquare,
          iconColor: 'primary' as const,
          onClick: () => setShowCreatePackageDialog(true),
        },
        {
          label: t('manageLibrary'),
          icon: faFolderGear,
          iconColor: 'primary' as const,
          onClick: () => setShowManageLibraryDialog(true),
        },
      ],
    },
  ];

  const additionalContent = (
    <>
      <CreatePackageDialog
        open={showCreatePackageDialog}
        onClose={() => setShowCreatePackageDialog(false)}
        onSuccess={(packageId) => {
          setShowCreatePackageDialog(false);
          addToCart({
            id: packageId,
            type: 'package',
            name: '', // The parent component will fetch the full details
            header: 'Packages',
          });
        }}
      />
      <Dialog
        title={t('manageLibrary')}
        open={showManageLibraryDialog}
        onClose={() => {
          setShowManageLibraryDialog(false);
        }}
      >
        {selectableItemTypes.map((itemType) => {
          if (!itemType.manageLink) return null;
          return (
            <ListItem
              key={itemType.value}
              primary={itemType.pluralLabel}
              component={Link}
              to={itemType.manageLink}
              onClick={() => {
                setSelectedItemType(itemType.value);
                setCurrentStep(PaymentStep.SelectItems);
              }}
              sx={{ p: 1 }}
              rightAdornment={
                <Icon
                  sx={{ color: (theme) => theme.palette.grey[500] }}
                  icon={faArrowUpRight}
                />
              }
              rounded
              clickable
            ></ListItem>
          );
        })}
      </Dialog>
    </>
  );

  return (
    <SectionedItemSelector
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      sections={sections}
      additionalContent={additionalContent}
      sx={{ mt: { sx: 1, md: 0 } }}
    />
  );
};

export default TypeSelector;
